<template>
  <div class="a-show-more__wrapper">
    <div
      v-if="isSsrPreviewVisible"
      class="a-show-more__content-wrapper"
      :style="previewDynamicStyle"
    >
      <slot />
    </div>

    <div
      ref="content"
      class="a-show-more__content-wrapper"
      :class="contentDynamicClass"
      :style="contentDynamicStyle"
    >
      <slot />

      <a-link
        v-if="isInlineVisible && to"
        :to="to"
        :directive="directive"
        :open-in-new-tab="openInNewTab"
        class="a-show-more__link"
        :style="triggerDynamicStyle"
        >{{ TRIGGER_TEXT.LIMITED }}</a-link
      >
      <button
        v-else-if="isInlineVisible"
        class="a-show-more__button"
        :style="triggerDynamicStyle"
        @click="changeHeight"
      >
        {{ triggerButtonText }}
      </button>

      <div
        v-if="isInlineOverflow"
        class="a-show-more__resizer"
        :class="triggerDynamicClass"
      >
        <template v-if="!isContentExpanded">
          <span class="a-show-more__gradient" />
          <div class="a-show-more__dots-wrapper">
            <span class="a-show-more__dots" :style="dotsDynamicStyle">...</span>
          </div>
        </template>

        <div class="a-show-more__action">
          <a-link
            v-if="to"
            :to="to"
            :directive="directive"
            :open-in-new-tab="openInNewTab"
            class="a-show-more__link"
            :style="triggerDynamicStyle"
            >{{ TRIGGER_TEXT.LIMITED }}</a-link
          >
          <button
            v-else
            class="a-show-more__button"
            :style="triggerDynamicStyle"
            @click="changeHeight"
          >
            {{ triggerButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { propValidator, PROP_TYPES } from '@/utils/validators'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import { REL_ATTRIBUTE_BACKEND_ENUM } from '@fmpedia/enums'

const TRIGGER_TEXT = {
  LIMITED: 'Show More',
  FULL: 'Show Less'
}

export default {
  name: 'AShowMore',
  mixins: [hydrationHelpers],
  props: {
    rowCount: propValidator([PROP_TYPES.NUMBER], false, 0),
    rowHeight: propValidator([PROP_TYPES.NUMBER], false),
    fontSize: propValidator([PROP_TYPES.NUMBER], false),
    to: propValidator([PROP_TYPES.STRING, PROP_TYPES.OBJECT], false),
    directive: propValidator(
      [PROP_TYPES.STRING],
      false,
      REL_ATTRIBUTE_BACKEND_ENUM.FOLLOW
    ),
    openInNewTab: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  data() {
    return {
      TRIGGER_TEXT,
      isSsrPreviewVisible: true,
      contentHeight: 0,
      isInlineVisible: false,
      contentHeightWithInlineAction: 0,
      isCalculating: true,
      isContentOverflow: false,
      isInlineOverflow: false,
      isContentExpanded: false
    }
  },
  computed: {
    limitedHeight() {
      return this.rowCount * this.rowHeight
    },
    contentDynamicClass() {
      return {
        'visibility-hidden': this.isCalculating
      }
    },
    previewDynamicStyle() {
      if (this.rowCount) {
        return {
          height: `${this.limitedHeight}px`
        }
      }
      return {}
    },
    contentDynamicStyle() {
      return {
        ...(!this.isCalculating && !this.isContentExpanded
          ? this.previewDynamicStyle
          : {})
      }
    },
    dotsDynamicStyle() {
      return {
        ...(!this.isCalculating && !this.isContentExpanded
          ? { lineHeight: `${this.rowHeight}px` }
          : {})
      }
    },
    triggerDynamicClass() {
      return { 'next-line': this.isContentExpanded && !this.to }
    },
    triggerDynamicStyle() {
      if (!this.rowHeight) return {}

      return {
        'line-height': `${this.rowHeight}px`,
        ...(this.fontSize ? { 'font-size': `${this.fontSize}px` } : {})
      }
    },
    triggerButtonText() {
      return this.isContentExpanded ? TRIGGER_TEXT.FULL : TRIGGER_TEXT.LIMITED
    }
  },
  watch: {
    $_hydrationHelpers_windowWidth: {
      handler() {
        this.isCalculating = true
        this.$nextTick(this.getContentHeight)
      }
    }
  },
  methods: {
    getContentHeight() {
      /**
       * if rowCount = 0 we don't limit text and don't show "Show More"
       */
      if (this.rowCount === 0) {
        this.isContentExpanded = true
        this.dontShowTrigger()
        return
      }
      if (!this.$refs.content) return

      this.contentHeight = this.$refs.content.clientHeight
      /**
       * if content height larger then limited height we use absolute position of
       * "Show More" trigger
       */
      if (this.contentHeight > this.limitedHeight) {
        this.useAbsolutePosition()
        return
        /**
         * if content height is less or equal to limited height and trigger is not a link
         * we don't show "Show More" trigger at all
         */
      } else if (!this.to) {
        this.dontShowTrigger()
        return
      }

      /**
       * if content height is less or equal to limited height and trigger is a link
       * we check if possible to use inline or absolute position of the trigger
       */
      this.isInlineVisible = true
      this.$nextTick(this.getContentHeightWithInlineAction)
    },
    getContentHeightWithInlineAction() {
      if (!this.$refs.content) return

      this.contentHeightWithInlineAction = this.$refs.content.clientHeight
      if (this.contentHeight === this.contentHeightWithInlineAction) {
        this.useInlinePosition()
      } else {
        this.useAbsolutePosition()
      }
    },
    useInlinePosition() {
      this.isInlineVisible = true
      this.isInlineOverflow = false
      this.endCalculation()
    },
    useAbsolutePosition() {
      this.isInlineVisible = false
      this.isInlineOverflow = true
      this.endCalculation()
    },
    dontShowTrigger() {
      this.isInlineVisible = false
      this.isInlineOverflow = false
      this.endCalculation()
    },
    endCalculation() {
      this.isCalculating = false
      this.isSsrPreviewVisible = false
    },
    changeHeight() {
      this.isContentExpanded = !this.isContentExpanded
      const event = this.isContentExpanded ? 'expand' : 'collapse'
      this.$emit(event)
    }
  },
  mounted() {
    this.$nextTick(this.getContentHeight)
  }
}
</script>

<style lang="scss" scoped>
.a-show-more__wrapper {
  position: relative;
  max-width: 100%;

  .a-show-more__content-wrapper {
    position: relative;
    overflow: hidden;

    > * {
      display: inline-block;
    }
  }

  .a-show-more__resizer {
    position: absolute;
    bottom: 0;
    right: 0;
    display: inline-flex;
    align-items: flex-end;
    vertical-align: bottom;

    &.next-line {
      position: relative;
      display: block;
      bottom: unset;
      right: unset;
    }
  }

  .a-show-more__gradient {
    display: inline-block;
    width: 130px;
    height: 24px;
    background: linear-gradient(to right, transparent, rgba($c--white, 0.7));
    content: '';
  }

  .a-show-more__dots-wrapper {
    display: inline-block;
    background: white;
    padding-right: 5px;
    padding-left: 1px;
  }

  .a-show-more__dots {
    display: inline-block;
    background: linear-gradient(to right, rgba(black, 0.5), rgba(black, 0.3));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .a-show-more__action {
    display: inline-block;
    color: $c--main;
    background: white;
  }

  .inline {
    display: inline;
  }

  .inline-block {
    display: inline-block;
  }

  .visibility-hidden {
    position: absolute;
    top: 0;
    left: 0;
    visibility: hidden;
  }

  .a-show-more__link,
  .a-show-more__button {
    display: inline;
    font-size: 16px;
    color: $c--main;

    @include mobile {
      font-size: 14px;
    }
  }
}
</style>
